/**
 * Formatting utilities for campaign reports
 * Handles currency, numbers, and percentages with proper formatting
 */

/**
 * Format a number as GBP currency
 * Handles millions with M suffix (e.g., £2.57M)
 */
export function formatCurrencyGBP(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '£0.00';
  
  const num = typeof value === 'string' ? parseFloat(value.replace(/[£,\s]/g, '')) : value;
  if (isNaN(num)) return '£0.00';
  
  // Format millions with M suffix
  if (num >= 1000000) {
    return `£${(num / 1000000).toFixed(2)}M`;
  }
  
  // Format thousands with K suffix
  if (num >= 1000) {
    return `£${(num / 1000).toFixed(1)}k`;
  }
  
  return `£${num.toFixed(2)}`;
}

/**
 * Format a number in compact notation
 * Examples: 11.1M, 15,624, 130,200
 */
export function formatNumberCompact(value: number | string | null | undefined): string {
  if (value === null || value === undefined) return '0';
  
  const num = typeof value === 'string' ? parseFloat(value.replace(/[,.\s]/g, '')) : value;
  if (isNaN(num)) return '0';
  
  // Format millions with M suffix
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  
  // Format thousands with K suffix
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  
  // Format with commas for readability
  return num.toLocaleString('en-GB');
}

/**
 * Format a number as a percentage
 * Examples: 0.42%, 15.4%, 81%
 */
export function formatPercent(value: number | string | null | undefined, decimals: number = 1): string {
  if (value === null || value === undefined) return '0%';
  
  const num = typeof value === 'string' ? parseFloat(value.replace(/[%,\s]/g, '')) : value;
  if (isNaN(num)) return '0%';
  
  return `${num.toFixed(decimals)}%`;
}

/**
 * Parse a string number that may contain commas, currency symbols, or other formatting
 */
export function parseNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  
  // Remove currency symbols, commas, spaces, tildes
  const cleaned = value.toString().replace(/[£$,\s~]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}

/**
 * Parse a percentage string (e.g., "15.4%", "81%") to a number
 */
export function parsePercentage(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') return value;
  
  const cleaned = value.toString().replace(/[%,\s]/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? 0 : num;
}
